/** Static copy from PCL 25-26 31-08-26.xlsm — sheets K0 / K1 / K_1 */

import type {G0Page} from '@/pages/components/audit-program/g-section/constants';

export type KSectionPageCode = 'K0' | 'K1' | 'K_1';

export const K_SECTION_PAGES: KSectionPageCode[] = ['K0', 'K1', 'K_1'];

export const K_PAGE_TITLES: Record<KSectionPageCode, string> = {
    K0: 'Audit programme - Bank balances and cash',
    K1: 'Bank balance',
    K_1: 'Bank account detail',
};

export type KProgrammePage = G0Page;

export const K0_PAGE: KProgrammePage = {
    title: 'AUDIT PROGRAMME - BANK BALANCES AND CASH',
    rows: [
        {id: 'r1', kind: 'section', num: '', text: 'General', level: 0},
        {id: 'r2', kind: 'main', num: '1', text: 'Obtain and check, or prepare, a lead schedule for the current year\'s figures and reconcile this to the general ledger.', level: 0},
        {id: 'r3', kind: 'main', num: '2', text: 'Carry out analytical procedures such as:', level: 0},
        {id: 'r4', kind: 'sub', num: '(a)', text: 'comparison of the current figures with those of prior periods;', level: 1},
        {id: 'r5', kind: 'sub', num: '(b)', text: 'review and comparison of key ratios or other performance indicators.', level: 1},
        {id: 'r6', kind: 'main', num: '3', text: 'Review for items above performance materiality or that are otherwise unusual and verify.', level: 0},
        {id: 'r7', kind: 'section', num: '', text: 'Valuation and existence', level: 0},
        {id: 'r8', kind: 'main', num: '4', text: 'Obtain directly from the bank confirmations for all bank accounts open at any time during the year according to the external confirmation procedures in HKSA 505.7.', level: 0},
        {id: 'r9', kind: 'main', num: '5', text: 'Obtain or prepare bank reconciliations for all accounts and verify.', level: 0},
        {id: 'r10', kind: 'sub', num: '(a)', text: 'Check bank balances to bank statement and bank confirmation.', level: 1},
        {id: 'r11', kind: 'sub', num: '(b)', text: 'Check cash book balance to general ledger.', level: 1},
        {id: 'r12', kind: 'sub', num: '(c)', text: 'Check casting of the reconciliations.', level: 1},
        {id: 'r13', kind: 'sub', num: '(d)', text: 'Check a sample of uncleared items through to the new period (noting dates).  Obtain reason where any item has taken longer than expected to clear.', level: 1},
        {id: 'r14', kind: 'sub', num: '(e)', text: 'Obtain explanations and substantiate all adjustments on the bank reconciliations.', level: 1},
        {id: 'r15', kind: 'main', num: '6', text: 'Ensure that all matters dealt with in the bank confirmations have been referenced to the relevant schedule/sections.', level: 0},
        {id: 'r16', kind: 'main', num: '7', text: 'Ensure that bank balances and overdrafts are only netted off where a formal right of set-off exists.', level: 0},
        {id: 'r17', kind: 'main', num: '8', text: 'Ensure that the company has not exceeded any restrictions on borrowing powers imposed in the Articles of Association or any loan agreement.', level: 0},
        {id: 'r18', kind: 'section', num: '', text: 'Cash', level: 0},
        {id: 'r19', kind: 'main', num: '9', text: 'For businesses receiving cash income, ensure unbanked takings before and after the year end have been accounted for in the correct period.', level: 0},
        {id: 'r20', kind: 'main', num: '10', text: 'For businesses receiving cash income, ensure that all unbanked takings at the year end have been timeously banked in the new period.', level: 0},
        {id: 'r21', kind: 'main', num: '11', text: 'Obtain certificates / confirmations for all cash balances not counted.', level: 0},
        {id: 'r22', kind: 'section', num: '', text: 'Foreign Currencies', level: 0},
        {id: 'r23', kind: 'main', num: '12', text: 'Ensure the entity accounted for foreign currency transactions in accordance with applicable accounting standards.', level: 0},
        {id: 'r24', kind: 'main', num: '13', text: 'Confirm that all monetary assets have been translated at the closing rate.', level: 0},
        {id: 'r25', kind: 'section', num: '', text: 'Presentation and disclosure', level: 0},
        {id: 'r26', kind: 'main', num: '14', text: 'Confirm that a disclosure checklist will be completed for this year. Where that is not the case explain how the objectives relating to disclosure will be achieved.', level: 0},
        {id: 'r27', kind: 'main', num: '15', text: 'Ensure that there is evidence on the file to support all disclosures made.', level: 0},
        {id: 'r28', kind: 'main', num: '16', text: 'Review the bank confirmations and correspondence and ensure that all necessary disclosures have been made.', level: 0},
        {id: 'r29', kind: 'section', num: '', text: 'Bespoke tests', level: 0},
        {id: 'r30', kind: 'main', num: '17', text: 'Draft and perform any necessary additional tests such as those required by C8.3 and cross reference with the objectives on the summary sheet.', level: 0},
        {id: 'r31', kind: 'section', num: '', text: 'Conclusion', level: 0},
        {id: 'r32', kind: 'main', num: '18', text: 'Consider whether there are any points which need to be included in a letter of representation or letter of comment and record on A5 or A6 as appropriate.', level: 0},
        {id: 'r33', kind: 'main', num: '19', text: 'Review the planned extent of reliance on internal controls in this area and consider whether this remains appropriate.', level: 0},
        {id: 'r34', kind: 'main', num: '20', text: 'Assess whether the initial materiality and/or risk assessment should be revised in view of the audit evidence obtained.  Record details of any necessary adjustments on B6 or B9.  Consider the impact on the remainder of the audit work and on any work undertaken to date.', level: 0},
    ],
};

export type KRemark = {tick: string; text: string};

export type K1Page = {
    title: string;
    cyLabel: string;
    lyLabel: string;
    blankAccountRows: number;
    remarksTitle: string;
    remarks: KRemark[];
};

export type KDetailPage = {
    title: string;
    subtitle?: string;
    accountLabel: string;
    cyLabel: string;
    lyLabel: string;
    conclusionLabel: string;
    conclusionPlaceholder: string;
    remarksTitle: string;
    remarks: KRemark[];
};

export const K1_PAGE: K1Page = {
    title: 'Bank balance',
    cyLabel: 'Current year HK$',
    lyLabel: 'Last year HK$',
    blankAccountRows: 8,
    remarksTitle: 'Remark',
    remarks: [
        {tick: 'C/✓', text: 'Bank confirmation sent / Bank confirmed'},
        {tick: 'l', text: 'Per ledger'},
        {tick: '<', text: 'Per bank statement'},
    ],
};

export const K_1_PAGE: KDetailPage = {
    title: 'Bank account detail',
    subtitle: 'Bank',
    accountLabel: 'Bank account',
    cyLabel: 'Current year HK$',
    lyLabel: 'Last year HK$',
    conclusionLabel: 'Conclusion',
    conclusionPlaceholder: 'The amount is fairly presented',
    remarksTitle: 'Remark',
    remarks: [
        {tick: '<', text: 'Per bank statement'},
        {tick: 'V', text: 'Checked to invoices'},
        {tick: '✓', text: 'Confirmed and agreed'},
        {tick: 'b', text: "Per last year's working paper"},
        {tick: '>', text: 'Per ledger'},
        {tick: '^', text: 'Casting checked'},
    ],
};
